import { BehaviorSubject, finalize, Observable, shareReplay } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env';
import { HttpClient, HttpContext } from '@angular/common/http';
import { InventoryOperationCreateInput, InventoryOperationListRes, InventoryOperationProfile } from '../types/inventory.type';
import { buildHttpParams } from '@app/shared/utils';
import { SKIP_ERROR_TOKEN } from '@app/shared/interceptors/auth.interceptor';

@Injectable()
export class InventoryApiService {
	readonly #httpClient = inject(HttpClient);

	readonly #loadingSub$ = new BehaviorSubject(false);
	readonly #loadingProfileSub$ = new BehaviorSubject(false);

	loadingList$ = this.#loadingSub$.asObservable().pipe(
		shareReplay(1)
	)
	loadingProfile$ = this.#loadingProfileSub$.asObservable().pipe(
		shareReplay(1)
	)

	fetchOperations(shopId: number): Observable<InventoryOperationListRes> {
		const params = buildHttpParams({ status: 'DRAFT', type: 'INVENTORY', shops: shopId, limit: 1000 });

		this.#loadingSub$.next(true);
		return this.#httpClient.get<InventoryOperationListRes>(`${environment.apiUrl}/shops/operations`, { params }).pipe(
			finalize(() => this.#loadingSub$.next(false))
		);
	}

	createOperation(operation: Partial<InventoryOperationCreateInput>): Observable<number> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.post<number>(`${environment.apiUrl}/shops/operation/INVENTORY`, operation).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	updateOperationComment({ id_operation, comment }: { id_operation: number, comment: string | null }): Observable<number> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.patch<number>(`${environment.apiUrl}/shops/operation/INVENTORY/${id_operation}`, { comment }).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	validateOperation({ id_operation, force }: { id_operation: number, force: boolean }): Observable<number> {
		this.#loadingProfileSub$.next(true);

		const params: Record<string, boolean> = force ? { force: true } : { };
		let context = new HttpContext();
		if (!force) {
			context = context.set(SKIP_ERROR_TOKEN, true)
		}

		return this.#httpClient.post<number>(`${environment.apiUrl}/shops/operation/INVENTORY/${id_operation}/commit`, { }, { context, params }).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	deleteOperation({ id_operation }: { id_operation: number }): Observable<number> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.delete<number>(`${environment.apiUrl}/shops/operation/INVENTORY/${id_operation}`).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	fetchOperationProfile({ id_operation }: { id_operation: number }): Observable<InventoryOperationProfile> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.get<InventoryOperationProfile>(`${environment.apiUrl}/shops/inventory/${id_operation}`).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	addOperationProduct(
		{ id_operation, input }:
		{
			id_operation: number,
			input: { id_product: number, stock: number }
		}
	): Observable<number> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.post<number>(`${environment.apiUrl}/shops/operation/INVENTORY/${id_operation}/product`, input).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	updateOperationProduct(
		{ id_operation, id_reference, input }:
		{
			id_operation: number,
			id_reference: number,
			input: { id_product: number, stock: number }
		}
	): Observable<number> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.patch<number>(`${environment.apiUrl}/shops/operation/INVENTORY/${id_operation}/product/${id_reference}`, input).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	deleteOperationProduct(
		{ id_operation, id_reference }:
		{ id_operation: number, id_reference: number }
	): Observable<number> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.delete<number>(`${environment.apiUrl}/shops/operation/INVENTORY/${id_operation}/product/${id_reference}`).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}
}
